import { SliderState, useSliderState } from 'react-stately';
import {
  mergeProps,
  useFocusRing,
  useNumberFormatter,
  useSlider,
  useSliderThumb,
  VisuallyHidden,
} from 'react-aria';
import { RefObject, useRef } from 'react';
import { NumberFormatOptions } from '@internationalized/number';
import { SliderProps } from '@react-types/slider';

export interface MySliderProps extends SliderProps {
  formatOptions?: NumberFormatOptions;
  format?: (value: number) => string;
}

export default function Slider({
  format,
  formatOptions,
  ...props
}: MySliderProps) {
  const trackRef = useRef(null);
  const numberFormatter = useNumberFormatter(formatOptions);
  const state = useSliderState({
    ...props,
    numberFormatter: format ? { ...numberFormatter, format } : numberFormatter,
  });
  const { groupProps, trackProps, labelProps, outputProps } = useSlider(
    props,
    state,
    trackRef
  );

  return (
    <>
      <style>
        {`
      .slider {
        display: flex;
        
      }
      
      .slider.horizontal {
        flex-direction: column;
        width: full;
      }
      
      .slider.vertical {
        height: 150px;
      }
      
      .label-container {
        display: flex;
        justify-content: space-between;
      }
      
      .slider.horizontal .track {
        height: 30px;
        width: 100%;
      }
      
      /* track line */
      .track:before {
        content: attr(x);
        display: block;
        position: absolute;
        background: #84A7A1;
      }
      
      .slider.horizontal .track:before {
        height: 3px;
        width: 100%;
        top: 50%;
        transform: translateY(-50%);
      }
      
      .slider.vertical .track {
        width: 30px;
        height: 100%;
      }
      
      .slider.vertical .track:before {
        width: 3px;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
      }
      
      .thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #0E2954;
      }
      
      .thumb.dragging {
        background: #1F6E8C;
      }
      
      .thumb.focus {
        background: #2E8A99;
      }
      
      .slider.horizontal .thumb {
        top: 50%;
      }
      
      .slider.vertical .thumb {
        left: 50%;
      }
      
      .track.disabled {
        opacity: 0.4;
      }
      `}
      </style>
      <div {...groupProps} className={`slider ${state.orientation}`}>
        {/* Create a container for the label and output element. */}
        {props.label && (
          <div className="label-container">
            <label {...labelProps} className="text-sm">
              {props.label}
            </label>
            <output {...outputProps} className="text-xs text-gray-500">
              {state.getThumbValueLabel(0)}
            </output>
          </div>
        )}
        {/* The track element holds the visible track line and the thumb. */}
        <div
          {...trackProps}
          ref={trackRef}
          className={`track ${state.isDisabled ? 'disabled' : ''}`}
        >
          <Thumb state={state} index={0} trackRef={trackRef} />
        </div>
      </div>
    </>
  );
}

interface ThumbProps {
  index: number | undefined;
  trackRef: RefObject<Element>;
  state: SliderState;
}
function Thumb(props: ThumbProps) {
  const { state, trackRef, index } = props;
  const inputRef = useRef(null);
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
    },
    state
  );

  const { focusProps, isFocusVisible } = useFocusRing();
  return (
    <div
      {...thumbProps}
      className={`thumb ${isFocusVisible ? 'focus' : ''} ${
        isDragging ? 'dragging' : ''
      }`}
    >
      <VisuallyHidden>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
    </div>
  );
}
